import type { Track, TrackProcessor, VideoProcessorOptions } from "livekit-client";
import { portraitCrop } from "../primitives/portrait-crop";

const FPS = 30;

/**
 * A ticker that keeps time in a background tab.
 *
 * Timers on the page are clamped to once a second when the tab is hidden, and a
 * host alt-tabbing to check an order would freeze every viewer's picture. A
 * worker's timer is not clamped that way.
 */
function tick(every: number, fn: () => void): () => void {
  try {
    const url = URL.createObjectURL(new Blob([`setInterval(() => postMessage(0), ${every});`]));
    const worker = new Worker(url);
    worker.onmessage = fn;
    return () => {
      worker.terminate();
      URL.revokeObjectURL(url);
    };
  } catch {
    const timer = setInterval(fn, every);
    return () => clearInterval(timer);
  }
}

/**
 * Every broadcast goes out 9:16, whatever the camera gives.
 *
 * Asking the camera for portrait is only a request, and a laptop webcam answers
 * it with 16:9 or a square. So the frame is cut here: the middle 9:16 of
 * whatever arrives, drawn to a canvas, and the canvas is what is published.
 *
 * A LiveKit processor rather than a track of our own, because the processor is
 * carried across a camera switch and a restart: LiveKit swaps the source and
 * calls `restart`, and the published track never changes.
 */
export class PortraitProcessor implements TrackProcessor<Track.Kind.Video, VideoProcessorOptions> {
  readonly name = "portrait-crop";
  processedTrack?: MediaStreamTrack;
  #video: HTMLVideoElement | null = null;
  #canvas: HTMLCanvasElement | null = null;
  #stop: (() => void) | null = null;

  async init(opts: VideoProcessorOptions): Promise<void> {
    const canvas = document.createElement("canvas");
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    this.#canvas = canvas;
    this.#video = video;
    await this.#source(opts.track);
    this.#draw();
    this.processedTrack = canvas.captureStream(FPS).getVideoTracks()[0];
    this.#stop = tick(1000 / FPS, this.#draw);
  }

  async restart(opts: VideoProcessorOptions): Promise<void> {
    await this.#source(opts.track);
  }

  async destroy(): Promise<void> {
    this.#stop?.();
    this.#stop = null;
    this.processedTrack?.stop();
    if (this.#video) this.#video.srcObject = null;
    this.#video = null;
    this.#canvas = null;
  }

  async #source(track: MediaStreamTrack): Promise<void> {
    if (!this.#video) return;
    this.#video.srcObject = new MediaStream([track]);
    await this.#video.play().catch(() => {});
  }

  #draw = () => {
    const video = this.#video;
    const canvas = this.#canvas;
    if (!video || !canvas || video.videoWidth === 0) return;
    const crop = portraitCrop(video.videoWidth, video.videoHeight);
    if (canvas.width !== crop.width || canvas.height !== crop.height) {
      canvas.width = crop.width;
      canvas.height = crop.height;
    }
    canvas.getContext("2d")?.drawImage(video, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);
  };
}
